import { ReactElement, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { ApiKeyListDto, KeyStatus } from "../../../data/clients/ExternalApiClient";
import { PageShell } from "./shared/PageShell";
import { KpiTile } from "./shared/KpiTile";
import { StatusPill } from "./shared/StatusPill";
import {
  formatCompact,
  formatInt,
  formatMoney,
  formatRelative,
  planSummary,
} from "./shared/formatters";
import { CreateKeyDialog } from "./components/CreateKeyDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

type StatusFilter = "all" | KeyStatus;

export function ApiKeysListScreen(): ReactElement {
  const { apiKeyRepository } = useRepositoriesContext();
  const navigate = useNavigate();

  const keys = apiKeyRepository.useKeys();
  const dashboard = apiKeyRepository.useDashboardSummary();
  const knownScopes = apiKeyRepository.useKnownScopes();

  const [createOpen, setCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const data = keys.data ?? [];
    return data.filter((k) => {
      if (statusFilter !== "all" && k.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !k.name.toLowerCase().includes(q) &&
          !k.keyIdPrefix.toLowerCase().includes(q) &&
          !String(k.organizationId ?? "").includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [keys.data, statusFilter, search]);

  const counts = useMemo(() => {
    const all = keys.data ?? [];
    return {
      all: all.length,
      Active: all.filter((k) => k.status === "Active").length,
      Blocked: all.filter((k) => k.status === "Blocked").length,
      Expired: all.filter((k) => k.status === "Expired").length,
    };
  }, [keys.data]);

  const revenueByCurrency = dashboard.data?.revenueByCurrency ?? [];
  const primaryRev = revenueByCurrency[0];

  return (
    <PageShell
      eyebrow="Administration · Ekstern API"
      title="API-nøgler"
      subtitle="Opret, scope, prissæt og overvåg hver eksterne API-bruger. Gratis-tiers har en hård grænse; betalte planer akkumuleres til næste faktura."
      actions={
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-white bg-success hover:brightness-95 rounded-sm transition-all"
        >
          <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
          Opret nøgle
        </button>
      }
    >
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiTile
          label="Aktive nøgler"
          value={
            <span>
              {formatInt(dashboard.data?.activeKeys ?? counts.Active)}
              <span className="text-slate-300 mx-1">/</span>
              <span className="text-slate-400 text-xl">
                {formatInt(dashboard.data?.totalKeys ?? counts.all)}
              </span>
            </span>
          }
          loading={dashboard.isLoading}
          hint={
            dashboard.data &&
            `${dashboard.data.blockedKeys} blokeret · ${dashboard.data.expiredKeys} udløbet`
          }
        />
        <KpiTile
          label="Kald i denne måned"
          value={formatCompact(dashboard.data?.mtdCalls)}
          loading={dashboard.isLoading}
          hint={
            dashboard.data &&
            `siden ${new Date(dashboard.data.monthStart).toLocaleDateString("da-DK", { day: "2-digit", month: "short" })}`
          }
        />
        <KpiTile
          label={`Omsætning i denne måned${primaryRev ? ` (${primaryRev.currency})` : ""}`}
          value={
            primaryRev ? formatMoney(primaryRev.total, primaryRev.currency) : "—"
          }
          loading={dashboard.isLoading}
          hint={
            revenueByCurrency.length > 1 &&
            `+${revenueByCurrency.length - 1} ${revenueByCurrency.length > 2 ? "andre valutaer" : "anden valuta"}`
          }
        />
        <KpiTile
          label="Gratis-tier"
          value={
            <span>
              {formatInt(
                (keys.data ?? []).filter(
                  (k) => k.plan?.planType === "FreeTierCap",
                ).length,
              )}
            </span>
          }
          loading={keys.isLoading}
          hint="nøgler på gratis-planer med grænse"
        />
      </section>

      <section className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-1 p-0.5 bg-slate-100 rounded-sm w-fit">
            {(["all", "Active", "Blocked", "Expired"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider rounded-sm transition-colors ${
                  statusFilter === s
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {s === "all"
                  ? "Alle"
                  : s === "Active"
                    ? "Aktive"
                    : s === "Blocked"
                      ? "Blokerede"
                      : "Udløbne"}
                <span className="ml-1.5 font-mono tabular-nums text-[10px] text-slate-400">
                  {s === "all" ? counts.all : counts[s]}
                </span>
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-72">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søg på navn, præfiks eller forhandler-ID…"
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-sm focus:outline-none focus:border-slate-400 focus:bg-white"
            />
          </div>
        </div>

        {keys.isLoading ? (
          <SkeletonRows rows={6} />
        ) : keys.isError ? (
          <div className="px-5 py-12 text-center text-sm text-rose-600">
            Kunne ikke indlæse nøgler. Tjek admin-API-nøglens konfiguration.
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState onCreate={() => setCreateOpen(true)} />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold border-b border-slate-200">
                <th className="text-left px-5 py-3">Nøgle</th>
                <th className="text-left px-3 py-3">Forhandler</th>
                <th className="text-left px-3 py-3">Plan</th>
                <th className="text-left px-3 py-3">Scopes</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-right px-5 py-3">Sidst brugt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((k) => (
                <KeyRow
                  key={k.id}
                  data={k}
                  onClick={() => navigate(`/admin/api-keys/${k.id}`)}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <CreateKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={apiKeyRepository.createKey}
        knownScopes={knownScopes.data ?? []}
      />
    </PageShell>
  );
}

function KeyRow({
  data,
  onClick,
}: {
  data: ApiKeyListDto;
  onClick: () => void;
}): ReactElement {
  return (
    <tr
      onClick={onClick}
      className="border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50/60 transition-colors"
    >
      <td className="px-5 py-4">
        <div className="font-medium text-slate-900 text-sm">{data.name}</div>
        <div className="font-mono text-[11px] text-slate-500 mt-0.5">
          {data.keyIdPrefix}
        </div>
      </td>
      <td className="px-3 py-4 font-mono text-xs text-slate-700">
        {data.organizationId ?? "—"}
      </td>
      <td className="px-3 py-4 text-xs text-slate-700">
        {planSummary(data.plan)}
      </td>
      <td className="px-3 py-4">
        <div className="flex flex-wrap gap-1 max-w-[220px]">
          {data.scopes.slice(0, 3).map((s) => (
            <span
              key={s}
              className="px-1.5 py-0.5 text-[10px] font-mono text-slate-600 bg-slate-100 rounded-sm"
            >
              {s}
            </span>
          ))}
          {data.scopes.length > 3 && (
            <span className="px-1.5 py-0.5 text-[10px] font-mono text-slate-400">
              +{data.scopes.length - 3}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-4">
        <StatusPill status={data.status} />
      </td>
      <td className="px-5 py-4 text-right font-mono text-xs text-slate-500 tabular-nums">
        {formatRelative(data.lastUsedAt)}
      </td>
    </tr>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): ReactElement {
  return (
    <div className="px-5 py-20 flex flex-col items-center text-center">
      <div className="relative w-16 h-16 mb-5">
        <div className="absolute inset-0 border border-slate-200 rounded-sm" />
        <div className="absolute inset-2 border border-slate-200 rounded-sm" />
        <div className="absolute inset-4 border border-slate-300 rounded-sm flex items-center justify-center text-slate-400 font-mono text-sm">
          ⌗
        </div>
      </div>
      <div className="text-sm font-medium text-slate-900">
        Ingen nøgler matcher dette filter
      </div>
      <div className="mt-1.5 text-xs text-slate-500 max-w-sm">
        Opret en nøgle for at give en forhandler adgang til den eksterne API.
        Hver nøgle faktureres efter den tilknyttede plan.
      </div>
      <button
        onClick={onCreate}
        className="mt-6 inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-white bg-success hover:brightness-95 rounded-sm transition-all"
      >
        <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
        Opret nøgle
      </button>
    </div>
  );
}

function SkeletonRows({ rows }: { rows: number }): ReactElement {
  return (
    <div className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="grid grid-cols-[1.5fr_0.6fr_1fr_1fr_0.6fr_0.8fr] gap-4 px-5 py-4 items-center"
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <div className="space-y-1.5">
            <div className="h-3.5 w-32 bg-slate-100 rounded-sm animate-pulse" />
            <div className="h-2.5 w-24 bg-slate-100 rounded-sm animate-pulse" />
          </div>
          <div className="h-3 w-12 bg-slate-100 rounded-sm animate-pulse" />
          <div className="h-3 w-28 bg-slate-100 rounded-sm animate-pulse" />
          <div className="flex gap-1">
            <div className="h-3 w-12 bg-slate-100 rounded-sm animate-pulse" />
            <div className="h-3 w-12 bg-slate-100 rounded-sm animate-pulse" />
          </div>
          <div className="h-3 w-16 bg-slate-100 rounded-sm animate-pulse" />
          <div className="h-3 w-20 bg-slate-100 rounded-sm animate-pulse justify-self-end" />
        </div>
      ))}
    </div>
  );
}
